/**
 * questionnaireAggregation — utility functions for aggregating questionnaire responses.
 * Provides per-question-type aggregation and common preference detection.
 */
import type {
  QuestionnaireResponse,
  QuestionnaireStep,
  AggregatedResponses,
  QuestionAggregation,
  StepResponse,
} from '@/types/pm-types';

/**
 * Aggregate all questionnaire responses into per-question summaries.
 * Handles each question type:
 *   - single-select / multi-select: value counts
 *   - rating: average + per-star distribution
 *   - number: average + min/max distribution
 *   - text: list of responses
 *   - boolean: value counts (true/false)
 *   - date: list of date values
 */
export function aggregateResponses(
  responses: QuestionnaireResponse[],
  steps: QuestionnaireStep[],
): AggregatedResponses {
  const perQuestion: Record<string, QuestionAggregation> = {};
  const allQuestions = steps.flatMap((s) => s.questions);

  for (const question of allQuestions) {
    const values: unknown[] = [];
    const valueCounts: Record<string, number> = {};

    for (const response of responses) {
      for (const [, stepResp] of Object.entries(response.responses)) {
        const answer = (stepResp as StepResponse).answers?.[question.id];
        if (answer === undefined || answer === null) continue;

        if (Array.isArray(answer)) {
          for (const v of answer) {
            values.push(v);
            valueCounts[String(v)] = (valueCounts[String(v)] || 0) + 1;
          }
        } else {
          values.push(answer);
          valueCounts[String(answer)] = (valueCounts[String(answer)] || 0) + 1;
        }
      }
    }

    let average: number | undefined;
    let distribution: Record<string, number> | undefined;

    if (question.type === 'rating' || question.type === 'number') {
      const nums = values.map(Number).filter((n) => !isNaN(n));
      if (nums.length > 0) {
        average = nums.reduce((a, b) => a + b, 0) / nums.length;
        distribution = {
          min: Math.min(...nums),
          max: Math.max(...nums),
        };
        // Rating distribution: count per star value (1-5)
        if (question.type === 'rating') {
          const dist: Record<string, number> = {};
          for (const n of nums) dist[String(n)] = (dist[String(n)] || 0) + 1;
          distribution = dist;
        }
      }
    }

    // Sort date responses chronologically
    const sortedValues = question.type === 'date'
      ? [...values].sort((a, b) => String(a).localeCompare(String(b)))
      : values;

    perQuestion[question.id] = {
      questionId: question.id,
      questionLabel: question.label,
      type: question.type,
      responses: sortedValues,
      valueCounts: Object.keys(valueCounts).length > 0 ? valueCounts : undefined,
      average,
      distribution,
    };
  }

  return {
    totalRespondents: responses.length,
    completedCount: responses.filter((r) => r.status === 'completed').length,
    perQuestion,
  };
}

/**
 * Find option values selected by at least `minAgreement` fraction of respondents.
 * Useful for "Common Ground" sections showing majority preferences.
 *
 * @param responses   All questionnaire responses
 * @param questionId  The question to analyze
 * @param minAgreement Minimum fraction (0-1) of respondents who selected the value (default 0.5)
 * @returns Array of option values meeting the agreement threshold, sorted by count descending
 */
export function findCommonPreferences(
  responses: QuestionnaireResponse[],
  questionId: string,
  minAgreement = 0.5,
): string[] {
  if (responses.length === 0) return [];

  const valueCounts: Record<string, number> = {};

  for (const response of responses) {
    for (const [, stepResp] of Object.entries(response.responses)) {
      const answer = (stepResp as StepResponse).answers?.[questionId];
      if (answer === undefined || answer === null) continue;

      if (Array.isArray(answer)) {
        for (const v of answer) {
          valueCounts[String(v)] = (valueCounts[String(v)] || 0) + 1;
        }
      } else {
        valueCounts[String(answer)] = (valueCounts[String(answer)] || 0) + 1;
      }
    }
  }

  return Object.entries(valueCounts)
    .filter(([, count]) => count / responses.length >= minAgreement)
    .sort((a, b) => b[1] - a[1])
    .map(([val]) => val);
}

/**
 * Export a flat CSV string from responses + steps.
 * Columns: respondentId, respondentName, status, completedAt, then one column per question.
 */
export function exportResponsesCsv(
  responses: QuestionnaireResponse[],
  steps: QuestionnaireStep[],
): string {
  const allQuestions = steps.flatMap((s) => s.questions);
  const headers = ['respondentId', 'respondentName', 'status', 'completedAt',
    ...allQuestions.map((q) => q.label)];

  const rows = responses.map((response) => {
    const base = [
      response.respondentId,
      response.respondentName,
      response.status,
      response.completedAt || '',
    ];
    const answers = allQuestions.map((q) => {
      for (const [, stepResp] of Object.entries(response.responses)) {
        const answer = (stepResp as StepResponse).answers?.[q.id];
        if (answer !== undefined && answer !== null) {
          return Array.isArray(answer) ? answer.join('; ') : String(answer);
        }
      }
      return '';
    });
    return [...base, ...answers];
  });

  const escapeCsv = (val: string) => {
    if (val.includes(',') || val.includes('"') || val.includes('\n')) {
      return `"${val.replace(/"/g, '""')}"`;
    }
    return val;
  };

  return [
    headers.map(escapeCsv).join(','),
    ...rows.map((row) => row.map(escapeCsv).join(',')),
  ].join('\n');
}
